// ============================================
// Vendor CSV Export Utility
// Generates and downloads order data as CSV
// ============================================

import type { Order } from '../../types/order';

/**
 * Escape a value for CSV (wrap in quotes if it contains commas, quotes, or newlines)
 */
function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Format cents to dollars string
 */
function formatCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Build a full shipping address string from an order
 */
function formatAddress(order: Order): string {
  const addr = order.shippingAddress;
  if (!addr) return '';
  const parts = [
    addr.line1,
    addr.line2,
    addr.city,
    addr.state,
    addr.postal_code,
    addr.country,
  ].filter(Boolean);
  return parts.join(', ');
}

/**
 * Export an array of orders to a CSV file and trigger download
 */
export function exportOrdersCsv(orders: Order[]): void {
  const headers = [
    'Order ID',
    'Date',
    'Customer Name',
    'Email',
    'Bundle',
    'Status',
    'Total',
    'Shipping Address',
    'Tracking',
  ];

  const rows = orders.map((order) => [
    order.id,
    order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-US') : '',
    order.customerName || '',
    order.customerEmail,
    order.bundleName || '',
    order.status,
    formatCents(order.totalInCents),
    formatAddress(order),
    order.trackingNumber
      ? `${order.trackingCarrier || ''} ${order.trackingNumber}`.trim()
      : '',
  ]);

  // Build CSV string
  const csvLines = [
    headers.map(csvEscape).join(','),
    ...rows.map((row) => row.map(csvEscape).join(',')),
  ];
  const csvString = csvLines.join('\n');

  // Create blob and trigger download
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  const dateStr = new Date().toISOString().slice(0, 10);
  link.href = url;
  link.download = `hemologix-orders-${dateStr}.csv`;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
